import React, {useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import * as Styles from 'layouts/PrivateLayout.styles';
import {Typography} from '@mui/material';
import Button from 'components/ui/Button';
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import AutoComplete, {OptionType} from 'components/ui/AutoComplete';
import {useGetAvailableSlotsQuery, useGetLocationsQuery} from 'services/locations';
import Spinner from 'components/ui/Spinner';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import {enqueueSnackbar} from "notistack";

// Updated convertTo12HourFormat function
const convertTo12HourFormat = (time: string): string => {
    const [hours, minutes] = time.split(':');
    let hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12; // the hour '0' should be '12'
    return `${hour}:${minutes} ${ampm}`;
};

const SelectBookingDetails: React.FC<{ onDetailsSelected: (details: any) => void }> = ({ onDetailsSelected }) => {
    const navigate = useNavigate();
    const calendarRef = useRef<any>(null);
    const [locationsSelected, setLocationsSelected] = useState<any>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<{ startTime: string, endTime: string } | null>(null);
    const [currentDate, setCurrentDate] = useState<string | null>(null);

    const { data: availableSlots, isLoading: isLoadingSlots } = useGetAvailableSlotsQuery(
        { locationId: selectedLocationId!, date: selectedDate! },
        { skip: !selectedLocationId || !selectedDate }
    );

    const { data: dataLocations, isLoading: isLoadingLocations } = useGetLocationsQuery({ limit: 999 });

    const handleDateClick = (arg: any) => {
        if (!locationsSelected) {
            enqueueSnackbar("Please select a location first", { variant: 'error', autoHideDuration: 1000 });
            return;
        }
        setCurrentDate(arg.dateStr);
        setSelectedDate(arg.dateStr);
    };

    const handleSlotSelection = (slot: { startTime: string, endTime: string }) => {
        setSelectedSlot({
            startTime: convertTo12HourFormat(slot.startTime),
            endTime: convertTo12HourFormat(slot.endTime)
        });
    };

    const handleLocationsChange = (_event: React.ChangeEvent<{}>, value: any) => {
        setLocationsSelected(value);
        setSelectedLocationId(value?.id.toString() || null);
    };

    const handleConfirm = () => {
        if (!selectedLocationId || !selectedSlot || !selectedDate) {
            enqueueSnackbar("Please select a location, date, and time slot", { variant: 'error', autoHideDuration: 1000 });
            return;
        }
        onDetailsSelected({
            locationId: selectedLocationId,
            date: selectedDate,
            startTime: selectedSlot.startTime,
            endTime: selectedSlot.endTime,
            locationName: locationsSelected.label
        });
    };

    const locations: OptionType[] = dataLocations?.locations.map((row) => ({
        label: row.name,
        id: row.id
    })) || [];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 h-full container mx-auto">
            <div className="flex flex-col gap-10 border-r-2 p-6">
                <section>
                    {isLoadingLocations ? (
                        <Spinner />
                    ) : (
                        <AutoComplete
                            id="locations"
                            label="Locations"
                            placeholder="Select location"
                            value={locationsSelected}
                            onChange={handleLocationsChange}
                            options={locations}
                            limitTags={5}
                            multipleOptions={false}
                        />
                    )}
                </section>
                <section>
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin]}
                        initialView="dayGridMonth"
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth'
                        }}
                        selectable
                        dateClick={handleDateClick}
                    />
                </section>
            </div>
            <div className="flex flex-col gap-2 p-6 pb-32">
                <section>
                    <Typography sx={{ fontSize: 18 }}>Open Slots for date: {currentDate}</Typography>
                </section>
                {isLoadingSlots ? (
                    <Spinner />
                ) : availableSlots?.available_slots.length ? (
                    availableSlots?.available_slots?.map((slot: any) => (
                        <Button
                            key={slot.id}
                            startIcon={<CalendarTodayOutlined />}
                            cc-width="full"
                            onClick={() => handleSlotSelection(slot)}
                            cc-color={selectedSlot?.startTime === convertTo12HourFormat(slot.startTime) ? "purple" : undefined}
                        >
                            {convertTo12HourFormat(slot.startTime)} - {convertTo12HourFormat(slot.endTime)}
                        </Button>
                    ))
                ) : (
                    <Typography>No available slots for the selected date</Typography>
                )}
            </div>
            <Styles.FlexContainer flex-row-lg className="border-t-2 pt-4">
                <Styles.FlexContainerColumn push-right className="pr-4">
                    <Button onClick={() => navigate(-1)}>Cancel</Button>
                    <span className="mr-4"></span>
                    <Button cc-color="purple" onClick={handleConfirm} disabled={!selectedSlot}>
                        Confirm
                    </Button>
                </Styles.FlexContainerColumn>
            </Styles.FlexContainer>
        </div>
    );
};

export default SelectBookingDetails;
